// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

import isEqual from 'lodash/isEqual';
import { useRef } from 'react';

import type { ExtensionViewportSize } from '@atlaskit/renderer';

import { usePageContentId } from '@confluence/page-context';

import { useExtensionList, FORGE_MODULE_XEN_MACRO } from '../';

const DEFAULT_EXTENSION_VIEWPORT_SIZES: ExtensionViewportSize[] = [];

// Hook to retrieve forge app viewport sizes for initial SSR render
export const useExtensionViewportSizes = (skipQuery = false) => {
	const [contentId] = usePageContentId();
	const VIEWPORT_SIZE_KEY = `${FORGE_MODULE_XEN_MACRO}:viewportSizes:${contentId}`;

	const filteredExtensions =
		(window.__SSR_FORGE_EXTENSIONS__?.[VIEWPORT_SIZE_KEY]?.extensions as ExtensionViewportSize[]) ??
		DEFAULT_EXTENSION_VIEWPORT_SIZES;

	// Technically here cached value can have length of 0.
	// In this case, we don't want to skip the query as we have no way to tell whether its from cache or just default value.
	const hasCachedValue = filteredExtensions.length > 0;

	const { extensions, loading, error } = useExtensionList({
		moduleType: FORGE_MODULE_XEN_MACRO,
		skipQuery: skipQuery || hasCachedValue,
	});

	const prevExtensionViewportSizes = useRef(filteredExtensions);

	if (loading || error || !extensions) {
		return filteredExtensions;
	}

	const nextExtensionViewportSizes = extensions
		.map(({ id, properties }) =>
			properties?.viewportSize
				? { extensionId: id, viewportSize: properties.viewportSize }
				: undefined,
		)
		.filter(Boolean) as ExtensionViewportSize[];

	if (
		prevExtensionViewportSizes.current.length !== nextExtensionViewportSizes.length ||
		!isEqual(prevExtensionViewportSizes.current, nextExtensionViewportSizes)
	) {
		prevExtensionViewportSizes.current = nextExtensionViewportSizes;

		// Write to window object to cache the viewport sizes.
		if (window.__SSR_FORGE_EXTENSIONS__) {
			window.__SSR_FORGE_EXTENSIONS__ = {
				...window.__SSR_FORGE_EXTENSIONS__,
				[VIEWPORT_SIZE_KEY]: {
					extensions: nextExtensionViewportSizes,
				},
			};
		}
	}
	return prevExtensionViewportSizes.current;
};

export type { ExtensionViewportSize };
